/**
 * Palette values needed by JavaScript.
 *
 * Theming itself lives entirely in `styles/tailwind.css`: semantic tokens defined once on `:root`
 * and again under `.dark`, with the class toggled on <html>. This file exists only for the handful
 * of places that need a colour as a *value* rather than a class - currently just the
 * react-loading-skeleton theme, which takes props, not CSS.
 *
 * It used to also export `applyThemeVariables`, which wrote these colours as inline styles onto
 * <html> on every theme toggle. Inline styles beat stylesheet rules, so that quietly overrode the
 * `.dark` token block and gave the app two competing sources of truth for the same colours. The CSS
 * is the only one now.
 */

// Theme color definitions
export const themeColors = {
    // Color Palette - blue / grey / white
    brand: "var(--color-primary)",
    brandHover: "var(--color-primary-hover)",
    purple: "#1D4ED8",
    green: "#3DDC97",
    blue: "#2563EB",
    red: "#DC2626",

    // Light mode colors
    light: {
        1: "#E2E8F0",
        2: "#CBD5E1",
        3: "#FFFFFF",
    },

    // Dark mode colors
    dark: {
        1: "#0F172A",
        2: "#1E293B",
        3: "#334155",
    },
};

// Helper to get color value for use in styled-components (backwards compatibility)
export const getColor = (colorName: string, isDarkMode: boolean): string => {
    switch (colorName) {
        case 'background':
            return isDarkMode ? themeColors.dark[1] : themeColors.light[1];
        case 'background-back':
            return isDarkMode ? themeColors.dark[2] : themeColors.light[2];
        case 'background-front':
            return isDarkMode ? themeColors.dark[3] : themeColors.light[3];
        case 'background-contrast':
            return isDarkMode ? themeColors.light[3] : themeColors.dark[3];
        case 'text':
            return isDarkMode ? '#ffffff' : '#000000';
        case 'brand':
            return themeColors.brand;
        default:
            return colorName;
    }
};

// Backwards compatibility export (for components not yet migrated)
const Theme = {
    colors: themeColors,
    getColor,
};

export default Theme;
